// src/Pages/Courses_details/Courses_Details.jsx

import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../Firebase/firebaseConfig';
import { useTranslation } from 'react-i18next';
import { Alert, Modal } from 'react-bootstrap';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './CourseDetail.css';

const CoursesDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false); // State for Modal

  // Memoize formatted duration to optimize performance
  const formattedDuration = useMemo(() => {
    if (course?.duration?.amount && course.duration.unit) {
      return `${course.duration.amount} ${t(course.duration.unit)}`;
    }
    return '';
  }, [course, t]);

  // Fetch course details from Firebase
  useEffect(() => {
    const fetchCourseDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const docRef = doc(db, 'courses', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setCourse(docSnap.data());
        } else {
          setError(t('courseNotFound'));
        }
      } catch (err) {
        console.error('Error fetching course details:', err);
        if (err.code === 'permission-denied') {
          setError(t('permissionDenied'));
        } else if (err.code === 'network-request-failed') {
          setError(t('networkError'));
        } else {
          setError(t('failedToLoadCourse'));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCourseDetails();
  }, [id, t]); // Dependencies: id and t

  // Handle RTL and language settings
  useEffect(() => {
    const html = document.documentElement;
    html.lang = i18n.language;
    html.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
  }, [i18n.language]);

  // Function to safely get content in current language, fallback to English
  const getContent = (field) => {
    if (course[field]) {
      return course[field][i18n.language] || course[field]['en'] || '';
    }
    return '';
  };

  // Handler for Enroll button click
  const handleEnrollClick = () => {
    if (course.enrollmentOpen) {
      navigate(`/enroll/course/${id}`); // Navigate to Enroll Page
    } else {
      setShowModal(true); // Show Modal if Enrollment is Closed
    }
  };

  const handleCloseModal = () => setShowModal(false);

  const formatDate = (value) =>
    value ? new Date(value).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' }) : t('notSpecified');

  const renderBody = () => {
    if (loading) {
      return (
        <div className="loader-container">
          <div className="spinner"></div>
          <p>{t('loading')}</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="container error-container">
          <Alert variant="danger" className="error-message">{error}</Alert>
          <div className="error-buttons">
            <button className="btn btn-outline-primary me-2" onClick={() => navigate(-1)}>{t('goBack')}</button>
            <button className="btn btn-primary" onClick={() => navigate(0)}>{t('retry')}</button>
          </div>
        </div>
      );
    }

    if (!course) return null;

    const isOpen = !!course.enrollmentOpen;
    const syllabus = course.syllabus?.[i18n.language] || course.syllabus?.en || [];
    const title = getContent('title') || t('untitled');
    const ledger = [
      ['far fa-clock', t('duration'), formattedDuration || t('notSpecified')],
      ['far fa-calendar-check', t('startDate'), formatDate(course.startDate)],
      ['far fa-calendar-xmark', t('endDate'), formatDate(course.endDate)],
    ];

    return (
      <>
        {/* Hero */}
        <header className="cd-hero">
          <div className="container">
            <button className="cd-back" onClick={() => navigate(-1)}>
              <i className="fas fa-arrow-left"></i> {t('goBack', 'Back')}
            </button>
            <span className="cd-hero__eyebrow">{t('course', 'Course')}</span>
            <h1 className="course-title">{title}</h1>
            <h2 className="course-subtitle">{getContent('subtitle') || t('courseOverview')}</h2>
            <ul className="cd-chips">
              {formattedDuration && <li><i className="far fa-clock"></i> {formattedDuration}</li>}
              {course.instructor && <li><i className="far fa-user"></i> {course.instructor}</li>}
              <li className={`cd-chip-status ${isOpen ? 'open' : 'closed'}`}>
                {isOpen ? t('enrollmentOpen', 'Enrollment open') : t('enrollmentClosed')}
              </li>
            </ul>
          </div>
        </header>

        <div className="container cd-layout">
          {/* Main column */}
          <div className="cd-main">
            <section className="cd-section">
              <h3>{t('overview')}</h3>
              <p className="cd-lead">{getContent('overview') || t('noOverview')}</p>
            </section>

            <section className="cd-section">
              <h3>{t('syllabus')}</h3>
              {syllabus.length > 0 ? (
                <ol className="syllabus-list">
                  {syllabus.map((item, index) => (
                    <li key={index}>
                      <span className="syllabus-list__num">{String(index + 1).padStart(2, '0')}</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="no-content">{t('noSyllabus')}</p>
              )}
            </section>

            <section className="cd-section">
              <h3>{t('instructorInfo')}</h3>
              <div className="cd-instructor">
                <span className="cd-instructor__avatar"><i className="fas fa-user-graduate"></i></span>
                <div>
                  <p className="cd-instructor__name">{course.instructor || t('notAvailable')}</p>
                  <p className="cd-instructor__bio">{getContent('bio') || t('notAvailable')}</p>
                </div>
              </div>
            </section>
          </div>

          {/* Sticky enrollment card */}
          <aside className="cd-aside">
            <div className="cd-card">
              <h3>{t('courseDetails')}</h3>
              <dl className="cd-ledger">
                {ledger.map(([icon, label, value]) => (
                  <div className="cd-ledger__row" key={label}>
                    <dt><i className={icon}></i> {label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
                <div className="cd-ledger__row">
                  <dt><i className="fas fa-lock"></i> {t('enrollment', 'Enrollment')}</dt>
                  <dd className={`cd-status ${isOpen ? 'open' : 'closed'}`}>
                    {isOpen ? t('enrollmentOpen', 'Enrollment open') : t('enrollmentClosed')}
                  </dd>
                </div>
              </dl>

              <button
                className={`btn btn-primary enroll-button ${isOpen ? '' : 'is-closed'}`}
                onClick={handleEnrollClick}
                aria-label={`${t('enrollNow')} ${title}`}
              >
                {isOpen ? t('enrollNow') : t('enrollClosed')}
              </button>
              {!isOpen && (
                <button className="cd-contact" onClick={() => navigate('/contact')}>
                  {t('contactUs')} <i className="fas fa-arrow-right"></i>
                </button>
              )}
            </div>
          </aside>
        </div>
      </>
    );
  };

  return (
    <main className="course-details-container">
      {renderBody()}

      {/* Enrollment Closed Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('enrollmentClosed')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('enrollmentClosedMessage')}</p>
          <p>{t('contactUs')}</p>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-outline-primary" onClick={handleCloseModal}>{t('close')}</button>
          <button className="btn btn-primary" onClick={() => navigate('/contact')}>{t('contactUs')}</button>
        </Modal.Footer>
      </Modal>
    </main>
  );
};

export default CoursesDetails;
